import { PropType } from 'vue';
import mixins from 'vue-typed-mixins';
import _ from 'lodash';
import { FileExportMixin } from '@codequality/vue-cli-base';

import {
  Eフェーズ種別,
  Eモニタリング損益差異分析基準値,
  Eモニタリング業績予測基準値,
  E帳票種別,
  E補助科目フラグ,
} from '@/openapi/enum';
import {
  データApi,
  データListItemResponse,
  勘定科目Api,
  勘定科目ListItemResponse,
  部門Api,
  部門ListItemResponse,
  帳票Api,
  計画書データListItemResponse,
} from '@/openapi/user';

import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import フェーズ種別Mixin from '@/entries/user/mixins/フェーズ種別Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import AbstractデータService from '@/classes/service/AbstractデータService';
import 差異分析Service from '@/classes/service/モニタリング/差異分析Service';
import 業績予測Service from '@/classes/service/モニタリング/業績予測Service';
import 分析PlbscfService from '@/classes/service/分析/分析PlbscfService';
import { 償却FunctionType } from '@/classes/service/分析計画/償却Type';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';

export type PrintDialogStyle = {
  width: string;
  top: string;
  tableClass: string;
};

export type PrintConfig = {
  name: string;
  service: AbstractデータService<unknown>;
  with勘定科目?: boolean;
  with部門?: boolean;
  isOutput?: boolean;
  referenceValue?: typeof Eモニタリング損益差異分析基準値 | typeof Eモニタリング業績予測基準値;
  format?: string;
  option?: Record<string, string | number | boolean | AbstractデータService<unknown>>;
};

export type 償却Option = { functionType: 償却FunctionType };

export default mixins(FileExportMixin, RoutesMixin, 事業所Mixin, 計画書Mixin, フェーズ種別Mixin).extend({
  name: 'CデータPrint',

  props: {
    printDialogStyle: {
      type: Object as PropType<PrintDialogStyle>,
      required: true,
    },
    printConfigs: {
      type: Array as PropType<PrintConfig[]>,
      required: true,
    },
  },

  data(): {
    api: {
      d: データApi;
      k: 勘定科目Api;
      b: 部門Api;
      o: 帳票Api;
    };
    printDialogVisible: boolean;
  } {
    return {
      api: {
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
        b: UserApiUtility.ogApi(部門Api),
        o: UserApiUtility.ogApi(帳票Api),
      },
      printDialogVisible: false,
    };
  },

  computed: {
    hasReferenceValue(): boolean {
      return this.hasPrintConfigKey('referenceValue');
    },
    hasFormat(): boolean {
      return this.hasPrintConfigKey('format');
    },
  },

  created(): void {
    window.addEventListener('message', this.set計画書FromChildTab);
  },
  destroyed(): void {
    window.removeEventListener('message', this.set計画書FromChildTab);
  },

  methods: {
    hasPrintConfigKey(key: keyof PrintConfig): boolean {
      return this.printConfigs.filter((p) => p[key] !== undefined).length > 0;
    },
    set計画書FromChildTab(e: MessageEvent<{ v計画書: 計画書データListItemResponse }>): void {
      // 子タブから渡された計画書データで、親のstorage/observableを書き換え
      if (e.data === null || e.data === undefined || e.data.v計画書 === undefined) return;
      this.set計画書(e.data.v計画書);
    },

    openPrintDialog(): void {
      this.printDialogVisible = true;
    },

    beforeClose(): void {
      this.printDialogVisible = false;
      this.printConfigs.forEach((r) => this.$set(r, 'isOutput', false));
    },

    async $_output(e帳票種別: E帳票種別): Promise<void> {
      if (this.printConfigs.filter((p) => p.isOutput).length === 0) {
        window.alert('選択してください');
        return;
      }

      const outputs = this.printConfigs.filter((p) => p.isOutput);
      const 入力分類s = _.uniq(outputs.map((p) => p.service.getRelationalE入力分類s()).flat());
      let 勘定科目s: 勘定科目ListItemResponse[] = [];
      let 部門s: 部門ListItemResponse[] = [];
      const with勘定科目 = outputs.filter((p) => p.with勘定科目).length > 0;
      const with部門 = outputs.filter((p) => p.with部門).length > 0;

      const responses = await Promise.all([
        this.api.d.listデータ(this.計画書.pk, 入力分類s, this.apiフェーズ種別),
        with勘定科目
          ? this.api.k.list勘定科目(
              this.事業所.pk,
              this.eフェーズ種別 === Eフェーズ種別.分析フェーズ ? E補助科目フラグ.通常 : undefined
            )
          : null,
        with部門 ? this.api.b.list部門(this.事業所.pk) : null,
      ]);
      const データs: データListItemResponse[] = responses[0].data;
      if (responses[1] !== null) 勘定科目s = responses[1].data;
      if (responses[2] !== null) 部門s = responses[2].data;

      // 初期化
      分析PlbscfService.initialize();
      計画PlbscfService.initialize();
      業績予測Service.initialize();
      差異分析Service.initialize();

      // 同一serviceのparseが複数回呼ばれることは許容（Plbscfは許容しない）
      const requests = outputs.flatMap((v) => {
        if (v.with勘定科目) v.service.set勘定科目s = 勘定科目s;
        if (v.with部門) v.service.set部門s = 部門s;
        const parsed = v.service.parse帳票ListItemRequest(データs, v.option);
        const 帳票データs = Array.isArray(parsed) ? parsed : [parsed];
        return 帳票データs.map((p) => ({
          templateName: v.name,
          ...p,
        }));
      });

      const printResponse = await this.api.o.output帳票(e帳票種別, requests, this.getExportOptions());
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.exportFile(printResponse, process.env.VUE_APP_IS_MOCK === 'true');

      this.beforeClose();
    },

    async outputPdf(): Promise<void> {
      await this.$_output(E帳票種別.pdf);
    },

    async outputXlsx(): Promise<void> {
      await this.$_output(E帳票種別.xlsx);
    },
  },
});
